import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api, { API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/context/I18nContext";
import { BadgeCheck, Calendar, Building2, Download, RefreshCw, ChevronRight, Clock, CircleSlash, Music2 } from "lucide-react";
import LicenseCalculator from "@/components/LicenseCalculator";
import { getToken } from "@/lib/authStorage";

function isCurrentlyActive(lic) {
  if (lic?.status !== "active") return false;
  const end = new Date(lic.end_date);
  return !Number.isNaN(end.getTime()) && end >= new Date();
}

function activitiesLabel(lic) {
  if (lic?.activities?.length) {
    return lic.activities.map((a) => `${a.space_name ? `${a.space_name} - ` : ""}${a.category} (${a.surface_m2} mp)`).join(", ");
  }
  return lic?.activity_category || "-";
}

function formatDate(value) {
  if (!value) return "-";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value).slice(0, 10);
  return `${d.getDate()}.${String(d.getMonth() + 1).padStart(2, "0")}.${d.getFullYear()}`;
}

export default function Dashboard() {
  const { user, refreshLicense, activeLicense, hasActiveLicense } = useAuth();
  const { t } = useI18n();
  const loc = useLocation();
  const nav = useNavigate();
  const [licenses, setLicenses] = useState([]);
  const [payments, setPayments] = useState([]);
  const [polling, setPolling] = useState(false);
  const [showCalc, setShowCalc] = useState(false);

  const activeLicenses = licenses.filter(isCurrentlyActive);
  const visibleActiveLicenses = activeLicenses.length ? activeLicenses : (hasActiveLicense && activeLicense ? [activeLicense] : []);

  const loadLicenses = async () => {
    try {
      const { data } = await api.get("/licenses/me");
      setLicenses(data);
    } catch {}
  };

  const loadPayments = async () => {
    try {
      const { data } = await api.get("/payments/me");
      setPayments(data);
    } catch {}
  };

  useEffect(() => {
    loadLicenses();
    loadPayments();
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(loc.search);
    const sessionId = params.get("session_id");
    if (!sessionId) return;
    setPolling(true);
    let attempts = 0;
    const poll = async () => {
      attempts += 1;
      try {
        const { data } = await api.get(`/checkout/status/${sessionId}`);
        if (data.payment_status === "paid") {
          toast.success(t("common.paymentSuccess"));
          await refreshLicense();
          await Promise.all([loadLicenses(), loadPayments()]);
          setPolling(false);
          nav("/dashboard", { replace: true });
          return;
        }
        if (data.status === "expired") {
          toast.error(t("common.paymentFailed"));
          setPolling(false);
          return;
        }
        if (attempts < 30) setTimeout(poll, 2000);
        else setPolling(false);
      } catch {
        setPolling(false);
      }
    };
    poll();
  }, [loc.search]); // eslint-disable-line react-hooks/exhaustive-deps

  const downloadDocument = async (lic, type = "certificate") => {
    const token = getToken();
    try {
      const res = await fetch(`${API}/licenses/${lic.id}/${type}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error("Download failed");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${type === "contract" ? "Contract" : "Licenta"}-${lic.license_number}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Documentul nu a putut fi descarcat. Reincearca dupa reincarcarea paginii.");
    }
  };

  const downloadCert = (lic) => downloadDocument(lic, "certificate");
  const downloadContract = (lic) => downloadDocument(lic, "contract");
  const pendingPayments = payments.filter((p) => (
    ["creating", "pending", "unpaid", "draft_bank_transfer", "awaiting_transfer"].includes(p.payment_status)
    && !p.license_id
    && !(p.license_ids?.length)
  ));

  return (
    <div className="mx-auto max-w-7xl px-4 pb-32 pt-8 sm:px-6 lg:px-8">
      <header className="mb-8 flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <p className="text-xs uppercase tracking-[0.3em] text-[#D4A23A]">{user?.business_name}</p>
          <h1 className="mt-2 font-display text-4xl font-black tracking-tight sm:text-5xl">{t("dashboard.title")}</h1>
        </div>
        {visibleActiveLicenses.length === 0 && (
          <button
            data-testid="dashboard-activate-btn"
            onClick={() => setShowCalc((s) => !s)}
            className="rounded-full bg-[#D4A23A] px-5 py-2.5 text-sm font-semibold text-black transition hover:bg-[#E2B85C]"
          >
            {t("dashboard.activate")}
          </button>
        )}
      </header>

      {polling && (
        <div data-testid="payment-polling-banner" className="mb-6 flex items-center gap-3 rounded-2xl border border-[#D4A23A]/30 bg-[#D4A23A]/5 p-4">
          <span className="grid h-8 w-8 animate-spin place-items-center rounded-full border-2 border-[#D4A23A] border-t-transparent" />
          <p className="text-sm">{t("common.paymentPending")}</p>
        </div>
      )}

      {user && (
        <section className="mb-10" data-testid="dashboard-quick-actions">
          <QuickAction
            to="/music"
            icon={Music2}
            title={t("nav.musicAccess")}
            body="Biblioteca, favoritele si playlisturile sunt intr-un singur ecran, gata de redare."
            cta={t("nav.music")}
            primary
          />
        </section>
      )}

      <section className="mb-10">
        {visibleActiveLicenses.length > 0 ? (
          <div className="space-y-4">
            <div className="flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
              <div>
                <p className="text-xs uppercase tracking-[0.3em] text-[#D4A23A]">{t("dashboard.licenseActive")}</p>
                <h2 className="mt-2 font-display text-2xl font-bold">
                  {visibleActiveLicenses.length === 1 ? "1 licenta activa" : `${visibleActiveLicenses.length} licente active`}
                </h2>
              </div>
              <button
                data-testid="dashboard-add-license-btn"
                onClick={() => setShowCalc(true)}
                className="rounded-full bg-[#D4A23A] px-5 py-2.5 text-sm font-black text-black shadow-[0_0_28px_rgba(212,162,58,0.18)] transition hover:bg-[#E2B85C]"
              >
                {t("dashboard.activate")}
              </button>
            </div>
            <div className="grid gap-4 xl:grid-cols-2">
              {visibleActiveLicenses.map((lic) => (
                <ActiveLicenseCard
                  key={lic.id}
                  lic={lic}
                  onDownload={downloadCert}
                  onDownloadContract={downloadContract}
                  t={t}
                  onRenew={() => setShowCalc(true)}
                />
              ))}
            </div>
          </div>
        ) : (
          <NoLicenseCard t={t} onActivate={() => setShowCalc(true)} />
        )}
      </section>

      {showCalc && (
        <section className="mb-10">
          <h2 className="mb-4 font-display text-2xl font-bold">{t("dashboard.activate")}</h2>
          <LicenseCalculator />
        </section>
      )}

      {pendingPayments.length > 0 && (
        <section className="mb-10">
          <h2 className="mb-4 font-display text-2xl font-bold">Plati in asteptare</h2>
          <div className="grid gap-3">
            {pendingPayments.map((p) => (
              <div key={p.id} className="rounded-2xl border border-[#D4A23A]/20 bg-[#D4A23A]/5 p-4 text-sm text-zinc-300">
                <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                  <div>
                    <p className="font-semibold text-white">
                      {p.payment_method === "online_card" ? "Plata cu cardul este in curs" : "Licenta in asteptarea platii bancare"}
                    </p>
                    <p className="mt-1 text-xs text-zinc-400">{p.category} - {p.surface_m2} mp - {p.months} luni - {Number(p.amount).toFixed(2)} {p.currency?.toUpperCase()}</p>
                    <p className="mt-1 font-mono text-xs text-[#D4A23A]">Referinta plata: {p.bank_reference || p.session_id}</p>
                  </div>
                  <StatusPill status={p.payment_status} />
                </div>
                <p className="mt-3 text-xs text-zinc-500">Documentele si accesul complet devin disponibile dupa confirmarea platii de catre administrator.</p>
              </div>
            ))}
          </div>
        </section>
      )}

      <section className="mb-10">
        <h2 className="mb-4 font-display text-2xl font-bold">Documente si acorduri</h2>
        <div className="grid gap-4 rounded-2xl border border-white/5 bg-[#121212] p-6 sm:grid-cols-3">
          <Detail label="Termeni acceptati" value={user?.terms_accepted ? `Da - v${user?.terms_version || "1.0"}` : "Nu"} />
          <Detail label="GDPR acceptat" value={user?.gdpr_accepted ? `Da - v${user?.gdpr_version || "1.0"}` : "Nu"} />
          <Detail label="Documente electronice" value={user?.electronic_docs_accepted ? `Da - v${user?.electronic_docs_version || "1.0"}` : "Nu"} />
          <Detail label="Data acceptarii" value={user?.accepted_at ? user.accepted_at.slice(0, 16).replace("T", " ") : "-"} />
          <Detail label="IP acceptare" value={user?.accepted_ip || "-"} />
          <div className="text-sm">
            <span className="text-zinc-500">Documente legale</span>
            <div className="mt-1 flex gap-3">
              <Link to="/legal/terms" className="text-[#D4A23A] hover:underline">Termeni</Link>
              <Link to="/legal/privacy" className="text-[#D4A23A] hover:underline">GDPR</Link>
            </div>
          </div>
        </div>
      </section>

      <section className="mb-10">
        <h2 className="mb-4 font-display text-2xl font-bold">{t("dashboard.businessDetails")}</h2>
        <div className="grid gap-4 rounded-2xl border border-white/5 bg-[#121212] p-6 sm:grid-cols-2">
          <Detail label="Denumire" value={user?.business_name} />
          <Detail label="Persoana contact" value={user?.contact_person} />
          <Detail label="Email" value={user?.email} />
          <Detail label="Telefon" value={user?.phone} />
          <Detail label="Sediu social" value={user?.legal_address || user?.business_address} />
          <Detail label="CUI / TVA" value={user?.vat_number || "-"} />
        </div>
      </section>

      <section>
        <h2 className="mb-4 font-display text-2xl font-bold">{t("dashboard.history")}</h2>
        {licenses.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-white/10 p-12 text-center text-sm text-zinc-500">
            Nicio licenta emisa inca.
          </div>
        ) : (
          <div className="overflow-x-auto rounded-2xl border border-white/5 bg-[#121212]">
            <table className="w-full min-w-[760px] text-sm" data-testid="license-history-table">
              <thead className="border-b border-white/5 text-left text-xs uppercase tracking-widest text-zinc-500">
                <tr>
                  <th className="px-4 py-3 font-medium">{t("dashboard.licenseNo")}</th>
                  <th className="px-4 py-3 font-medium">Contract</th>
                  <th className="px-4 py-3 font-medium">Punct</th>
                  <th className="px-4 py-3 font-medium">{t("dashboard.category")}</th>
                  <th className="px-4 py-3 font-medium">mp</th>
                  <th className="px-4 py-3 font-medium">{t("dashboard.validUntil")}</th>
                  <th className="px-4 py-3 font-medium">Stare</th>
                  <th className="px-4 py-3" />
                </tr>
              </thead>
              <tbody>
                {licenses.map((l) => (
                  <tr key={l.id} className="border-b border-white/5 last:border-0 hover:bg-white/[0.02]">
                    <td className="px-4 py-3 font-mono text-xs">{l.license_number}</td>
                    <td className="px-4 py-3 font-mono text-xs text-zinc-400">{l.contract_number || "-"}</td>
                    <td className="px-4 py-3 text-zinc-300">{locationLabel(l)}</td>
                    <td className="px-4 py-3 text-zinc-300">{activitiesLabel(l)}</td>
                    <td className="px-4 py-3 font-mono text-zinc-400">{l.surface_m2}</td>
                    <td className="px-4 py-3 font-mono text-xs text-zinc-400">{formatDate(l.end_date)}</td>
                    <td className="px-4 py-3"><StatusPill status={l.status} /></td>
                    <td className="px-4 py-3 text-right">
                      <button onClick={() => downloadCert(l)} className="rounded-full p-2 text-zinc-400 transition hover:bg-white/5 hover:text-white" title={t("dashboard.downloadCert")}>
                        <Download size={14} />
                      </button>
                      <button onClick={() => downloadContract(l)} className="ml-1 rounded-full p-2 text-zinc-400 transition hover:bg-white/5 hover:text-white" title="Descarca contractul PDF">
                        <Download size={14} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}

function locationLabel(lic) {
  return [
    lic.locations_count ? `${lic.location_index}/${lic.locations_count}` : "",
    lic.venue_name || "",
  ].filter(Boolean).join(" - ") || "-";
}

function QuickAction({ to, icon: Icon, title, body, cta, primary = false }) {
  return (
    <Link
      to={to}
      className={`group flex items-center justify-between gap-4 rounded-2xl p-4 transition ${
        primary
          ? "border border-[#D4A23A]/35 bg-[#D4A23A] text-black hover:bg-[#E2B85C]"
          : "border border-white/5 bg-[#121212] hover:border-[#D4A23A]/40 hover:bg-[#171717]"
      }`}
    >
      <div className="flex min-w-0 items-center gap-4">
        <span className={`grid h-12 w-12 shrink-0 place-items-center rounded-2xl ${
          primary ? "bg-black/15 text-black" : "bg-[#D4A23A]/10 text-[#D4A23A]"
        }`}>
          <Icon size={22} />
        </span>
        <div className="min-w-0">
          <h3 className={`truncate font-display text-lg font-bold ${primary ? "text-black" : "text-white"}`}>{title}</h3>
          <p className={`mt-1 line-clamp-2 text-sm ${primary ? "text-black/70" : "text-zinc-400"}`}>{body}</p>
        </div>
      </div>
      <span className={`hidden shrink-0 rounded-full px-3 py-1.5 text-xs font-black transition sm:inline-flex ${
        primary ? "bg-black text-white" : "border border-white/10 text-zinc-300 group-hover:border-[#D4A23A]/40 group-hover:text-[#D4A23A]"
      }`}>
        {cta}
      </span>
    </Link>
  );
}

function ActiveLicenseCard({ lic, onDownload, onDownloadContract, t, onRenew }) {
  const days = Math.max(0, Math.floor((new Date(lic.end_date) - new Date()) / 86400000));
  return (
    <div data-testid="active-license-card" className="overflow-hidden rounded-3xl border border-[#D4A23A]/30 bg-gradient-to-br from-[#D4A23A]/10 via-[#121212] to-[#121212] p-5 sm:p-7">
      <div className="flex flex-col gap-6">
        <div>
          <div className="flex items-center gap-2">
            <BadgeCheck size={18} className="text-[#D4A23A]" />
            <span className="text-xs uppercase tracking-[0.3em] text-[#D4A23A]">{t("dashboard.licenseActive")}</span>
          </div>
          <h3 className="mt-3 font-display text-3xl font-black tracking-tight">{lic.license_number}</h3>
          <div className="mt-5 grid grid-cols-2 gap-4 text-sm">
            <Info icon={Building2} label="Punct" value={locationLabel(lic)} />
            <Info icon={Building2} label="Adresa" value={lic.business_address || "-"} />
            <Info icon={Building2} label={t("dashboard.category")} value={activitiesLabel(lic)} />
            <Info icon={Building2} label={t("dashboard.surface")} value={`${lic.surface_m2} mp`} />
            <Info icon={Calendar} label={t("dashboard.validUntil")} value={formatDate(lic.end_date)} />
            <Info icon={Clock} label="Ramase" value={`${days} zile`} />
            <Info icon={Calendar} label="Luni facturate" value={`${lic.billed_months || lic.months}`} />
            <Info icon={Calendar} label="Contract" value={lic.contract_number || "-"} />
          </div>
        </div>
        <div className="grid gap-2 sm:grid-cols-3">
          <button onClick={() => onDownload(lic)} className="flex items-center justify-center gap-2 rounded-full bg-[#D4A23A] px-4 py-2.5 text-sm font-semibold text-black transition hover:bg-[#E2B85C]">
            <Download size={14} /> {t("dashboard.downloadCert")}
          </button>
          <button onClick={() => onDownloadContract(lic)} className="flex items-center justify-center gap-2 rounded-full border border-white/15 px-4 py-2.5 text-sm text-white transition hover:bg-white/5">
            <Download size={14} /> Contract PDF
          </button>
          <button onClick={onRenew} className="flex items-center justify-center gap-2 rounded-full border border-white/15 px-4 py-2.5 text-sm text-white transition hover:bg-white/5">
            <RefreshCw size={14} /> {t("dashboard.renew")}
          </button>
        </div>
      </div>
    </div>
  );
}

function NoLicenseCard({ t, onActivate }) {
  return (
    <div data-testid="no-license-card" className="flex flex-col items-start gap-4 rounded-3xl border border-white/5 bg-[#121212] p-7 sm:flex-row sm:items-center sm:justify-between">
      <div>
        <div className="flex items-center gap-2">
          <CircleSlash size={16} className="text-zinc-500" />
          <span className="text-xs uppercase tracking-widest text-zinc-500">{t("dashboard.noLicense")}</span>
        </div>
        <p className="mt-2 max-w-md text-sm text-zinc-400">Activeaza o licenta pentru a difuza muzica in spatiul tau comercial.</p>
      </div>
      <button onClick={onActivate} data-testid="no-license-activate-btn" className="flex items-center gap-2 rounded-full bg-[#D4A23A] px-5 py-2.5 text-sm font-black text-black shadow-[0_0_28px_rgba(212,162,58,0.18)] transition hover:bg-[#E2B85C]">
        {t("dashboard.activate")} <ChevronRight size={14} />
      </button>
    </div>
  );
}

function Info({ icon: Icon, label, value }) {
  return (
    <div className="min-w-0">
      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-widest text-zinc-500">
        <Icon size={11} /> {label}
      </div>
      <p className="mt-1 break-words font-display text-sm font-bold text-white sm:text-base">{value}</p>
    </div>
  );
}

function Detail({ label, value }) {
  return (
    <div>
      <p className="text-[10px] uppercase tracking-widest text-zinc-500">{label}</p>
      <p className="mt-1 break-words text-sm text-white">{value || "-"}</p>
    </div>
  );
}

export function StatusPill({ status }) {
  const styles = {
    active: "bg-[#D4A23A] text-black",
    expired: "border border-white/10 text-zinc-400",
    pending: "border border-[#D4A23A]/30 bg-[#D4A23A]/10 text-[#D4A23A]",
    suspended: "border border-orange-500/30 bg-orange-500/10 text-orange-300",
    revoked: "border border-red-500/30 bg-red-500/10 text-red-300",
  }[status] || "border border-white/10 text-zinc-400";
  return (
    <span className={`inline-block rounded-full px-2 py-0.5 text-[10px] uppercase tracking-widest ${styles}`}>
      {status}
    </span>
  );
}
